import { Injectable } from '@angular/core';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import Polyline from '@arcgis/core/geometry/Polyline';
import Multipoint from '@arcgis/core/geometry/Multipoint';
import { footprintRing } from '../helpers/volume-geometry';

/**
 * GraphicFactoryService
 *
 * Factory service for creating ArcGIS Graphic objects.
 * Provides standardized methods for creating different geometry types
 * with consistent styling and color handling.
 */
@Injectable({
  providedIn: 'root'
})
export class GraphicFactoryService {

  /**
   * Convert hex color string to RGB array
   * @param hex - Hex color string (e.g., '#FF0000' or 'FF0000' or '#F00')
   * @returns Array [R, G, B] with values 0-255
   */
  hexToRgb(hex: string): [number, number, number] {
    // Remove the # if present
    hex = hex.replace(/^#/, '');

    // Handle 3-character hex codes (e.g., #RGB)
    if (hex.length === 3) {
      hex = hex.split('').map(char => char + char).join('');
    }

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return [r, g, b];
  }

  /**
   * Create a polygon graphic
   * @param coordinates - Ring coordinates for the polygon
   * @param isMultiPolygon - Whether coordinates represent a multi-polygon
   * @param isSourceList - Whether this is a source list polygon (affects outline width)
   * @param attributes - Optional attributes to attach to the graphic
   * @param hexColor - Optional hex color for the polygon
   * @param strokeWidth - Optional custom stroke width (overrides default)
   * @param opacity - Optional fill/outline opacity for a hex color (defaults 0.3 / 0.8)
   */
  createPolygonGraphic(
    coordinates: number[][][] | number[][][][],
    isMultiPolygon = false,
    isSourceList = false,
    attributes: Record<string, any> | null = null,
    hexColor: string | null = null,
    strokeWidth: number | null = null,
    opacity: { fill?: number; outline?: number } | null = null
  ): Graphic {
    // Determine colors based on provided hex color or default to blue
    let fillColor: number[];
    let outlineColor: number[];

    if (hexColor) {
      const rgb = this.hexToRgb(hexColor);
      fillColor = [...rgb, opacity?.fill ?? 0.3]; // 30% opacity for fill
      outlineColor = [...rgb, opacity?.outline ?? 0.8]; // 80% opacity for outline
    } else {
      // Default blue colors
      fillColor = [173, 216, 230, 0.3];
      outlineColor = isSourceList ? [0, 0, 255, 0.8] : [0, 0, 255, 0.3];
    }

    // A MultiPolygon is a list of polygons, each a list of rings: draw the rings
    // of every polygon, not just the first one
    const rings = isMultiPolygon
      ? ([] as number[][][]).concat(...(coordinates as number[][][][]))
      : coordinates as number[][][];
    const polygon = new Polygon({
      rings,
      spatialReference: { wkid: 4326 },
    });

    // Use custom strokeWidth if provided, otherwise use default based on isSourceList
    const outlineWidth = strokeWidth !== null ? strokeWidth : (isSourceList ? 1 : 2);

    return new Graphic({
      geometry: polygon,
      symbol: {
        // @ts-ignore - ArcGIS autocasting
        type: 'simple-fill',
        color: fillColor,
        outline: { color: outlineColor, width: outlineWidth },
      },
      attributes: attributes,
    });
  }

  /**
   * Create a point graphic
   * @param coordinates - [longitude, latitude] coordinates
   * @param hexColor - Optional hex color for the point marker
   * @param attributes - Optional attributes to attach to the graphic
   */
  createPointGraphic(
    coordinates: [number, number],
    hexColor: string | null = null,
    attributes: Record<string, any> | null = null,
    markerIconUrl: string | null = null,
    iconSize: { width?: number; height?: number; yOffset?: number } | null = null,
  ): Graphic {
    const point = new Point({
      longitude: coordinates[0],
      latitude: coordinates[1],
      spatialReference: { wkid: 4326 },
    });

    if (markerIconUrl) {
      const width = iconSize?.width ?? 26;
      const height = iconSize?.height ?? 36;
      const yoffset = iconSize?.yOffset ?? height / 2;
      return new Graphic({
        geometry: point,
        symbol: {
          // @ts-ignore - ArcGIS autocasting
          type: 'picture-marker',
          url: markerIconUrl,
          width: `${width}px`,
          height: `${height}px`,
          yoffset: `${yoffset}px`,
        },
        attributes: attributes,
      });
    }

    // Use provided color or default to blue
    let markerColor: number[];
    if (hexColor) {
      const rgb = this.hexToRgb(hexColor);
      markerColor = [...rgb, 0.8]; // 80% opacity
    } else {
      markerColor = [0, 0, 255, 0.8]; // Default blue
    }

    return new Graphic({
      geometry: point,
      symbol: {
        // @ts-ignore - ArcGIS autocasting
        type: 'simple-marker',
        style: 'circle',
        color: markerColor,
        size: '14px',
        outline: {
          color: [255, 255, 255, 0.9], // white outline — keeps point readable against polygons
          width: 2,
        },
      },
      attributes: attributes,
    });
  }

  /**
   * Create a multipoint graphic
   * @param coordinates - Array of [longitude, latitude] coordinates
   * @param hexColor - Optional hex color for the point markers
   * @param attributes - Optional attributes to attach to the graphic
   */
  createMultipointGraphic(
    coordinates: number[][],
    hexColor: string | null = null,
    attributes: Record<string, any> | null = null
  ): Graphic {
    const multipoint = new Multipoint({
      points: coordinates,
      spatialReference: { wkid: 4326 },
    });

    // Use provided color or default to blue
    let markerColor: number[];
    if (hexColor) {
      const rgb = this.hexToRgb(hexColor);
      markerColor = [...rgb, 0.8]; // 80% opacity
    } else {
      markerColor = [0, 0, 255, 0.8]; // Default blue
    }

    return new Graphic({
      geometry: multipoint,
      symbol: {
        // @ts-ignore - ArcGIS autocasting
        type: 'simple-marker',
        style: 'circle',
        color: markerColor,
        size: '10px',
        outline: {
          color: [0, 0, 0, 0.15], // subtle shadow effect
          width: 2,
        },
      },
      attributes: attributes,
    });
  }

  /**
   * Volume capture (mobile DA2 MultiPoint with altitudes): a filled convex-hull footprint
   * with the surveyed vertices drawn on top. Both graphics share `attributes` so every
   * existing hover / select / fade filter treats them as one feature.
   */
  createVolumeFootprintGraphics(
    coordinates: number[][],
    hexColor: string | null = null,
    attributes: Record<string, any> | null = null
  ): Graphic[] {
    const volumeAttributes = { ...(attributes || {}), is_volume: true };
    const ring = footprintRing(coordinates);
    const graphics: Graphic[] = [];

    if (ring.length >= 4) {
      const rgb = hexColor ? this.hexToRgb(hexColor) : [0, 145, 255];
      graphics.push(new Graphic({
        geometry: new Polygon({ rings: [ring], spatialReference: { wkid: 4326 } }),
        symbol: {
          // @ts-ignore - ArcGIS autocasting
          type: 'simple-fill',
          color: [...rgb, 0.35],
          outline: { color: [...rgb, 0.9], width: 2 },
        },
        attributes: volumeAttributes,
      }));
    }

    const dots = this.createMultipointGraphic(coordinates, hexColor, volumeAttributes);
    (dots.symbol as any).size = '7px';
    graphics.push(dots);
    return graphics;
  }

  /**
   * Create a polyline graphic
   * @param coordinates - Path coordinates for the polyline
   * @param isMultiLineString - Whether coordinates represent a multi-line string
   * @param hexColor - Optional hex color for the line
   * @param attributes - Optional attributes to attach to the graphic
   * @param strokeWidth - Optional custom stroke width (overrides default of 3)
   */
  createPolylineGraphic(
    coordinates: number[][] | number[][][],
    isMultiLineString = false,
    hexColor: string | null = null,
    attributes: Record<string, any> | null = null,
    strokeWidth: number | null = null,
    lineStyle: string | null = null,
    lineOpacity: number | null = null,
    dashTemplate: number[] | null = null
  ): Graphic {
    const paths = isMultiLineString ? coordinates : [coordinates];

    // Determine color based on provided hex color or default to blue
    let lineColor: number[];
    if (hexColor) {
      const rgb = this.hexToRgb(hexColor);
      lineColor = [...rgb, lineOpacity ?? 1]; // Full opacity for lines (matching Figma)
    } else {
      lineColor = [0, 0, 255, lineOpacity ?? 1]; // Default blue, full opacity
    }

    const polyline = new Polyline({
      paths: paths as number[][][],
      spatialReference: { wkid: 4326 },
    });

    // Use custom strokeWidth if provided, otherwise use default of 3
    const lineWidth = strokeWidth !== null ? strokeWidth : 3;

    // A fixed-pixel dash (dashTemplate) renders a clearly dashed line at any
    // zoom — unlike simple-line styles whose dashes scale with width and read
    // as solid on small mini-maps. Use a CIM symbol for this case.
    let symbol: any;
    if (dashTemplate && dashTemplate.length) {
      const rgb = hexColor ? this.hexToRgb(hexColor) : [0, 0, 255];
      const alpha255 = Math.round((lineOpacity ?? 1) * 255);
      symbol = {
        type: 'cim',
        data: {
          type: 'CIMSymbolReference',
          symbol: {
            type: 'CIMLineSymbol',
            symbolLayers: [
              {
                type: 'CIMSolidStroke',
                enable: true,
                width: lineWidth,
                color: [rgb[0], rgb[1], rgb[2], alpha255],
                capStyle: 'Butt',
                joinStyle: 'Round',
                effects: [
                  {
                    type: 'CIMGeometricEffectDashes',
                    dashTemplate,
                    lineDashEnding: 'NoConstraint',
                    scaleDash: false,
                  },
                ],
              },
            ],
          },
        },
      };
    } else {
      symbol = {
        type: 'simple-line',
        color: lineColor,
        width: lineWidth,
        ...(lineStyle ? { style: lineStyle } : {}),
      };
    }

    return new Graphic({
      geometry: polyline,
      symbol,
      attributes: attributes,
    });
  }
}
